import React, { useEffect, useMemo, useState } from 'react';
import { commesseAPI, clientsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ClientCombobox } from '../components/ClientCombobox';
import { ModelCombobox } from '../components/ModelCombobox';
import { ManageModelsDialog } from '../components/ManageModelsDialog';
import { WeeklyEmailDialog } from '../components/commesse/WeeklyEmailDialog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Loader2, Pencil, Trash2, ExternalLink, ClipboardList, Download, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PER_PAGE = 50;

const MONTHS = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const emptyForm = () => ({
    year: new Date().getFullYear(),
    number: '',
    data: new Date().toISOString().slice(0, 10),
    telaio: '', mod: '', client_id: '', client_name: '', naz: '', note: '', assigned_to: '',
});

const CommessePage = () => {
    const { isAdmin, canViewTeam } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [commesse, setCommesse] = useState([]);
    const [clients, setClients] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sellerFilter, setSellerFilter] = useState('all');
    const [yearFilter, setYearFilter] = useState('all');
    const [monthFilter, setMonthFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [exporting, setExporting] = useState(false);
    const [manageModelsOpen, setManageModelsOpen] = useState(false);

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const res = await commesseAPI.exportExcel({
                q: search,
                year: yearFilter,
                month: monthFilter,
                status: statusFilter,
                seller: sellerFilter,
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Commesse_${yearFilter !== 'all' ? yearFilter : new Date().getFullYear()}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Esportazione Excel completata');
        } catch (e) {
            toast.error('Errore nel download del file Excel');
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    useEffect(() => {
        const q = searchParams.get('q');
        if (q) setSearch(q);
    }, [searchParams]);

    const loadData = async () => {
        try {
            const [cRes, clRes, sRes] = await Promise.all([
                commesseAPI.getAll(),
                clientsAPI.getAll(),
                usersAPI.getSellers(),
            ]);
            setCommesse(cRes.data || []);
            setClients(clRes.data || []);
            setSellers((sRes.data || []).filter(u => u.role !== 'addetto_consegna'));
        } catch (e) {
            toast.error('Errore nel caricamento commesse');
        } finally {
            setLoading(false);
        }
    };

    const sellerName = (id) => sellers.find(s => s.id === id)?.name || '';
    const clientName = (c) => c.client_name || clients.find(cl => cl.id === c.client_id)?.company_name || '—';

    const currentYear = new Date().getFullYear();
    const formatCurrencyShort = (v) => {
        const n = v || 0;
        return n >= 1000 ? `${Math.round(n / 1000)}k €` : `${n} €`;
    };
    const todayStr = new Date().toISOString().slice(0, 10);
    const currentMonthStr = new Date().toISOString().slice(0, 7);
    const fmtDate = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        return isNaN(d) ? null : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    const yearOptions = useMemo(() => {
        const set = new Set(commesse.map(c => c.year).filter(Boolean));
        set.add(currentYear); set.add(currentYear + 1);
        return Array.from(set).sort((a, b) => b - a);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commesse]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        let list = commesse.filter(c => {
            if (statusFilter === 'libera' && c.status !== 'libera') return false;
            if (statusFilter === 'associata' && c.status !== 'associata') return false;
            if (statusFilter === 'inizio_produzione') {
                if (c.status !== 'associata' || !c.offer_production_date) return false;
            }
            if (statusFilter === 'da_consegnare') {
                if (c.status !== 'associata') return false;
                const dd = c.offer_delivery_date ? c.offer_delivery_date.slice(0, 10) : null;
                if (dd && dd < todayStr) return false; // già consegnata
            }
            if (statusFilter === 'consegnate') {
                if (c.status !== 'associata') return false;
                const dd = c.offer_delivery_date ? c.offer_delivery_date.slice(0, 10) : null;
                if (!dd || dd >= todayStr) return false;
            }
            if (statusFilter === 'consegne_mese') {
                if (c.status !== 'associata') return false;
                const dm = c.offer_delivery_date ? c.offer_delivery_date.slice(0, 7) : null;
                if (dm !== currentMonthStr) return false;
            }
            if (yearFilter !== 'all' && String(c.year) !== String(yearFilter)) return false;
            if (monthFilter !== 'all' && (c.data || '').slice(5, 7) !== monthFilter) return false;
            if (canViewTeam() && sellerFilter !== 'all' && c.assigned_to !== sellerFilter) return false;
            if (!term) return true;
            return (
                (c.number || '').toLowerCase().includes(term) ||
                (c.telaio || '').toLowerCase().includes(term) ||
                (c.mod || '').toLowerCase().includes(term) ||
                (c.naz || '').toLowerCase().includes(term) ||
                (clientName(c) || '').toLowerCase().includes(term)
            );
        });
        // Nella vista "Da consegnare" ordina per data di consegna crescente (prima le più imminenti)
        if (statusFilter === 'inizio_produzione') {
            list = [...list].sort((a, b) => (a.offer_production_date || '9999') < (b.offer_production_date || '9999') ? -1 : 1);
        }
        if (statusFilter === 'da_consegnare' || statusFilter === 'consegne_mese') {
            list = [...list].sort((a, b) => {
                const da = a.offer_delivery_date || '9999';
                const dbb = b.offer_delivery_date || '9999';
                return da < dbb ? -1 : da > dbb ? 1 : 0;
            });
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commesse, search, statusFilter, sellerFilter, yearFilter, monthFilter, clients]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const deliveryTimeline = useMemo(() => {
        if (statusFilter !== 'da_consegnare') return null;
        const monday = new Date();
        const dow = (monday.getDay() + 6) % 7; // 0 = lunedì
        monday.setDate(monday.getDate() - dow);
        monday.setHours(0, 0, 0, 0);
        const WEEKS = 6;
        const weeks = Array.from({ length: WEEKS }, (_, i) => {
            const s = new Date(monday);
            s.setDate(monday.getDate() + i * 7);
            return { start: s, count: 0, value: 0 };
        });
        let planning = 0, beyond = 0;
        filtered.forEach(c => {
            if (!c.offer_delivery_date) { planning++; return; }
            const dd = new Date(c.offer_delivery_date);
            const idx = Math.floor((dd - monday) / (7 * 24 * 3600 * 1000));
            if (idx < 0) { weeks[0].count++; weeks[0].value += c.offer_value || 0; }
            else if (idx < WEEKS) { weeks[idx].count++; weeks[idx].value += c.offer_value || 0; }
            else beyond++;
        });
        const maxCount = Math.max(1, ...weeks.map(w => w.count));
        return { weeks, planning, beyond, maxCount };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, filtered]);

    useEffect(() => { setPage(1); }, [search, statusFilter, sellerFilter, yearFilter, monthFilter]);

    const fetchNextNumber = async (year) => {
        try {
            const res = await commesseAPI.nextNumber(year);
            setForm(f => ({ ...f, number: res.data.number }));
        } catch { /* opzionale */ }
    };

    const openCreate = () => {
        const f = emptyForm();
        setEditing(null);
        setForm(f);
        setDialogOpen(true);
        fetchNextNumber(f.year);
    };
    const openEdit = (c) => {
        setEditing(c);
        setForm({
            year: c.year || new Date().getFullYear(),
            number: c.number || '',
            data: c.data || new Date().toISOString().slice(0, 10),
            telaio: c.telaio || '', mod: c.mod || '', client_id: c.client_id || '',
            client_name: c.client_name || '', naz: c.naz || '', note: c.note || '',
            assigned_to: c.assigned_to || '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                year: editing ? undefined : form.year,
                number: form.number || undefined,
                data: form.data || undefined,
                telaio: form.telaio || undefined,
                mod: form.mod || undefined,
                client_id: form.client_id || undefined,
                client_name: form.client_name || undefined,
                naz: form.naz || undefined,
                note: form.note || undefined,
                assigned_to: isAdmin() ? (form.assigned_to || undefined) : undefined,
            };
            if (editing) {
                await commesseAPI.update(editing.id, payload);
                toast.success('Commessa aggiornata');
            } else {
                const res = await commesseAPI.create(payload);
                toast.success(`Commessa creata: ${res.data.number}`);
            }
            setDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Errore nel salvataggio'));
        }
    };

    const handleDelete = async (c) => {
        if (!window.confirm(`Eliminare la commessa ${c.number}?`)) return;
        try {
            await commesseAPI.delete(c.id);
            toast.success('Commessa eliminata');
            loadData();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Errore nell\'eliminazione'));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="commesse-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight flex items-center gap-2">
                        <ClipboardList size={26} /> Commesse
                    </h1>
                    <p className="text-muted-foreground">{filtered.length} commesse · numero telaio</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {isAdmin() && (
                        <>
                            <Button variant="outline" className="font-heading" onClick={() => setManageModelsOpen(true)} data-testid="manage-models-btn">
                                <Settings2 size={18} className="mr-2" /> MODELLI
                            </Button>
                            <ManageModelsDialog open={manageModelsOpen} onOpenChange={setManageModelsOpen} />
                            <WeeklyEmailDialog />
                        </>
                    )}
                    <Button
                        variant="outline"
                        className="font-heading"
                        onClick={handleExportExcel}
                        disabled={exporting}
                        data-testid="export-commesse-excel-btn"
                    >
                        {exporting ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Download size={18} className="mr-2" />}
                        ESPORTA EXCEL
                    </Button>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        {canViewTeam() && (
                        <DialogTrigger asChild>
                            <Button className="btn-primary font-heading" onClick={openCreate} data-testid="add-commessa-btn">
                                <Plus size={20} className="mr-2" /> NUOVA COMMESSA
                            </Button>
                        </DialogTrigger>
                        )}
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase">
                                {editing ? `Modifica ${editing.number}` : 'Nuova Commessa'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            {editing ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Numero commessa</Label>
                                        <Input
                                            value={form.number}
                                            onChange={(e) => setForm({ ...form, number: e.target.value })}
                                            placeholder="es. B28726"
                                            data-testid="commessa-number-input"
                                        />
                                        <p className="text-[11px] text-muted-foreground mt-1">
                                            Puoi correggere il numero a mano (deve essere univoco).
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Data commessa</Label>
                                        <Input
                                            type="date"
                                            value={form.data}
                                            onChange={(e) => setForm({ ...form, data: e.target.value })}
                                            data-testid="commessa-data-input"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Numero commessa</Label>
                                        <Input
                                            value={form.number}
                                            onChange={(e) => setForm({ ...form, number: e.target.value })}
                                            placeholder="es. B28726 (vuoto = automatico)"
                                            data-testid="commessa-number-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Anno</Label>
                                        <Select value={String(form.year)} onValueChange={(v) => { setForm({ ...form, year: parseInt(v) }); fetchNextNumber(parseInt(v)); }}>
                                            <SelectTrigger data-testid="commessa-year"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map(y => (
                                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Data commessa</Label>
                                        <Input
                                            type="date"
                                            value={form.data}
                                            onChange={(e) => setForm({ ...form, data: e.target.value })}
                                            data-testid="commessa-data-input"
                                        />
                                    </div>
                                    <p className="text-[11px] text-muted-foreground col-span-2 -mt-1">
                                        Numero proposto in automatico come successivo dell'ultima commessa dell'anno (formato <b>B + numero + anno</b>). Puoi modificarlo o lasciarlo così.
                                    </p>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Telaio completo</Label>
                                    <Input
                                        value={form.telaio}
                                        onChange={(e) => setForm({ ...form, telaio: e.target.value })}
                                        placeholder="ZFJSBA..."
                                        data-testid="commessa-telaio"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Modello</Label>
                                    <ModelCombobox
                                        value={form.mod}
                                        onChange={(mod) => setForm({ ...form, mod })}
                                        testid="commessa-mod"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Cliente</Label>
                                <ClientCombobox
                                    clients={clients}
                                    value={form.client_id}
                                    onChange={(value) => setForm({ ...form, client_id: value })}
                                    testid="commessa-client"
                                />
                                <Input
                                    value={form.client_name}
                                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                                    placeholder="Oppure nome cliente libero (se non in anagrafica)"
                                    className="mt-2"
                                    data-testid="commessa-client-name"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Nazione</Label>
                                    <Input
                                        value={form.naz}
                                        onChange={(e) => setForm({ ...form, naz: e.target.value })}
                                        placeholder="I, D, PL..."
                                        data-testid="commessa-naz"
                                    />
                                </div>
                                {isAdmin() && (
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Commerciale</Label>
                                        <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                                            <SelectTrigger data-testid="commessa-seller">
                                                <SelectValue placeholder="Seleziona" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {sellers.map(s => (
                                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Note</Label>
                                <Textarea
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    rows={2}
                                    data-testid="commessa-note"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                                <Button type="submit" className="btn-primary" data-testid="commessa-submit">
                                    {editing ? 'SALVA' : 'CREA'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                        <div className="lg:col-span-2">
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Cerca</Label>
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Numero, telaio, modello, cliente, nazione..."
                                data-testid="commesse-search"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Anno</Label>
                            <Select value={String(yearFilter)} onValueChange={setYearFilter}>
                                <SelectTrigger data-testid="commesse-year-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti gli anni</SelectItem>
                                    {yearOptions.map(y => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Mese</Label>
                            <Select value={monthFilter} onValueChange={setMonthFilter}>
                                <SelectTrigger data-testid="commesse-month-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i mesi</SelectItem>
                                    {MONTHS.map((m, i) => (
                                        <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>{m}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider mb-2 block">Stato</Label>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger data-testid="commesse-status-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutte</SelectItem>
                                    <SelectItem value="libera">Libere</SelectItem>
                                    <SelectItem value="associata">Associate a offerta</SelectItem>
                                    <SelectItem value="inizio_produzione" data-testid="commesse-status-inizio-produzione">Data inizio produzione</SelectItem>
                                    <SelectItem value="da_consegnare">Da consegnare</SelectItem>
                                    <SelectItem value="consegne_mese">Consegne questo mese</SelectItem>
                                    <SelectItem value="consegnate">Consegnate</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {canViewTeam() && (
                            <div>
                                <Label className="text-xs uppercase tracking-wider mb-2 block">Commerciale</Label>
                                <Select value={sellerFilter} onValueChange={setSellerFilter}>
                                    <SelectTrigger data-testid="commesse-seller-filter"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tutti</SelectItem>
                                        {sellers.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Timeline consegne (vista Da consegnare) */}
            {deliveryTimeline && (
                <Card data-testid="delivery-timeline">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <ClipboardList size={16} className="text-amber-600" />
                            <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">Carico officina — prossime 6 settimane</span>
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {deliveryTimeline.weeks.map((w, i) => {
                                const label = w.start.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
                                const h = 8 + Math.round((w.count / deliveryTimeline.maxCount) * 56);
                                return (
                                    <div key={i} className="flex-1 min-w-[70px] flex flex-col items-center justify-end" data-testid={`timeline-week-${i}`}>
                                        <span className="text-sm font-bold text-foreground mb-1">{w.count}</span>
                                        <div
                                            className={`w-full rounded-t-sm ${w.count ? 'bg-amber-500' : 'bg-muted'}`}
                                            style={{ height: `${h}px` }}
                                            title={`${w.count} veicoli · ${formatCurrencyShort(w.value)}`}
                                        />
                                        <span className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap">{i === 0 ? 'Questa sett.' : label}</span>
                                    </div>
                                );
                            })}
                            {(deliveryTimeline.beyond > 0 || deliveryTimeline.planning > 0) && (
                                <div className="flex flex-col justify-end gap-1 pl-2 border-l ml-1">
                                    {deliveryTimeline.beyond > 0 && (
                                        <span className="text-[11px] px-2 py-1 rounded-sm bg-muted whitespace-nowrap" data-testid="timeline-beyond">Oltre 6 sett.: <b>{deliveryTimeline.beyond}</b></span>
                                    )}
                                    {deliveryTimeline.planning > 0 && (
                                        <span className="text-[11px] px-2 py-1 rounded-sm bg-amber-50 text-amber-700 whitespace-nowrap" data-testid="timeline-planning">Da pianificare: <b>{deliveryTimeline.planning}</b></span>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm data-table">
                            <thead>
                                <tr className="border-b bg-muted/30">
                                    <th className="text-left py-3 px-3">Commessa</th>
                                    <th className="text-left py-3 px-3">Data</th>
                                    <th className="text-left py-3 px-3">Telaio</th>
                                    <th className="text-left py-3 px-3">Modello</th>
                                    <th className="text-left py-3 px-3">Cliente</th>
                                    <th className="text-left py-3 px-3">Naz</th>
                                    {canViewTeam() && <th className="text-left py-3 px-3">Commerciale</th>}
                                    <th className="text-left py-3 px-3">Inizio produzione</th>
                                    <th className="text-left py-3 px-3">Consegna</th>
                                    <th className="text-left py-3 px-3">Stato</th>
                                    <th className="text-right py-3 px-3">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.length === 0 ? (
                                    <tr><td colSpan={canViewTeam() ? 11 : 10} className="text-center py-10 text-muted-foreground">Nessuna commessa</td></tr>
                                ) : pageItems.map((c) => {
                                    const dd = c.offer_delivery_date ? c.offer_delivery_date.slice(0, 10) : null;
                                    const delivered = dd && dd < todayStr;
                                    return (
                                    <tr key={c.id} className="border-b hover:bg-muted/20" data-testid={`commessa-row-${c.id}`}>
                                        <td className="py-2 px-3 font-medium whitespace-nowrap">{c.number}</td>
                                        <td className="py-2 px-3 whitespace-nowrap" data-testid={`commessa-data-${c.id}`}>{fmtDate(c.data) || '—'}</td>
                                        <td className="py-2 px-3 font-mono text-xs">{c.telaio || '—'}</td>
                                        <td className="py-2 px-3">{c.mod || '—'}</td>
                                        <td className="py-2 px-3">{clientName(c)}</td>
                                        <td className="py-2 px-3">{c.naz || '—'}</td>
                                        {canViewTeam() && <td className="py-2 px-3">{sellerName(c.assigned_to) || '—'}</td>}
                                        <td className="py-2 px-3 whitespace-nowrap" data-testid={`commessa-production-${c.id}`}>
                                            {c.status === 'associata' && c.offer_production_date ? fmtDate(c.offer_production_date) : '—'}
                                        </td>
                                        <td className="py-2 px-3 whitespace-nowrap" data-testid={`commessa-delivery-${c.id}`}>
                                            {c.status === 'associata'
                                                ? (fmtDate(c.offer_delivery_date)
                                                    ? <span className={delivered ? 'text-muted-foreground' : 'text-amber-700 font-medium'}>{fmtDate(c.offer_delivery_date)}</span>
                                                    : <span className="text-amber-700">Da pianificare</span>)
                                                : '—'}
                                        </td>
                                        <td className="py-2 px-3">
                                            {c.status === 'associata' ? (
                                                <Badge className="bg-blue-100 text-blue-700">Associata</Badge>
                                            ) : (
                                                <Badge variant="outline">Libera</Badge>
                                            )}
                                        </td>
                                        <td className="py-2 px-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {c.opportunity_id && (
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Vai alla commessa"
                                                        onClick={() => navigate(`/pipeline?offer=${c.opportunity_id}`)}
                                                        data-testid={`commessa-goto-offer-${c.id}`}>
                                                        <ExternalLink size={15} />
                                                    </Button>
                                                )}
                                                {canViewTeam() && (
                                                <>
                                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}
                                                    data-testid={`commessa-edit-${c.id}`}>
                                                    <Pencil size={15} />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                                                    onClick={() => handleDelete(c)} data-testid={`commessa-delete-${c.id}`}>
                                                    <Trash2 size={15} />
                                                </Button>
                                                </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="commesse-prev">
                        ← Precedente
                    </Button>
                    <span className="text-sm text-muted-foreground">Pagina {page} di {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} data-testid="commesse-next">
                        Successiva →
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CommessePage;
